import MuiUnfoldIcon from '@mui/icons-material/UnfoldMore'
import MuiList from '@mui/material/List'
import MuiListItem from '@mui/material/ListItem'
import MuiListItemIcon from '@mui/material/ListItemIcon'
import ListItemButton from '@mui/material/ListItemButton'
import MuiMenuItem from '@mui/material/MenuItem'
import MuiSelect from '@mui/material/Select'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox'
import { Avatar } from 'src/components/Atoms/Avatar'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import AddIcon from '@mui/icons-material/Add'

const MenuWrapper = styled(Box)(() => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
}))

const UserButtonWrapper = styled(FlexColumn)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  padding: `${theme.sizes.spacing.none} ${theme.sizes.spacing.none} ${theme.sizes.spacing['01']} `,
  alignItems: 'center',
  justifyContent: 'center',
  borderTop: `1px solid ${theme.colors.border.hover}`,
  '&.open': {
    borderTop: 'none',
  },
}))

const StyledList = styled(MuiList)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',

  gap: theme.sizes.spacing['01'],
  backgroundColor: theme.colors.surface.brand,
  borderRadius: theme.sizes.radius.md,
  transition: theme.transitions.create(['dimensions'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  }),
  '& .menuItem': {
    display: 'none',
  },
  '&:not(.open)': {
    width: '100%',

    padding: theme.sizes.spacing['04'],
  },
  '&.open': {
    width: `calc(100% - ${theme.sizes.spacing['07']})`,
    backgroundColor: theme.colors.surface.primary,
    padding: theme.sizes.spacing.none,
    margin: theme.sizes.spacing['04'],

    '& .menuItem': {
      display: 'flex',
    },
  },
}))

const StyledListItem = styled(MuiListItem)(({ theme }) => ({
  flex: '1',
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'stretch',
  width: '100%',
  margin: theme.sizes.spacing.none,
  padding: theme.sizes.spacing.none,
  '.open &:first-child': {
    paddingBottom: theme.sizes.spacing['04'],
    borderBottom: `1px solid ${theme.colors.border.secondary}`,
  },
  '& a': {
    display: 'flex',
    flex: '1 0 0',
  },
}))

const MenuHeaderButton = styled(ListItemButton)(({ theme }) => ({
  flex: '1',
  width: '100%',
  borderRadius: theme.sizes.radius.sm,
  display: 'flex',
  alignItems: 'center',
  height: 'auto',
  gap: theme.sizes.spacing.none,
  padding: theme.sizes.spacing['02'],
  margin: theme.sizes.spacing.none,
  '&:not(.listItem):hover': {
    backgroundColor: '#21201E',
  },
  '&.listItem': {
    paddingBottom: theme.sizes.spacing['07'],
    borderBottom: `1px solid ${theme.colors.border.secondary}`,
    borderRadius: 0,
  },
}))

const MenuItemButton = styled(ListItemButton)(({ theme }) => ({
  backgroundColor: theme.colors.surface.primary,
  display: 'flex',
  height: '2.5rem',
  borderRadius: theme.sizes.radius.sm,
  alignItems: 'center',
  alignSelf: 'stretch',
  gap: theme.sizes.spacing['06'],

  '& .MuiTypography-root': {
    color: theme.colors.text.secondary,
    fontFamily: theme.fonts.family.body,
    fontSize: theme.fonts.size.sm,
    fontStyle: 'normal',
    fontWeight: theme.fonts.weight.medium,
    lineHeight: theme.fonts.lineHeight['leading-5'],
    letterSpacing: theme.fonts.letterSpacing.wide,
  },
  '&:hover': {
    backgroundColor: theme.colors.surface.hover,
    '& .MuiTypography-root': {
      color: theme.colors.text.brand,
    },
  },
}))

const ExpandIcon = styled(MuiUnfoldIcon)(({ theme }) => ({
  color: theme.colors.icon.tertiary,
  '.MuiListItemButton-root:hover &': {
    color: theme.colors.text.inverted,
  },
}))

const UserItem = styled(FlexRow)(({ theme }) => ({
  width: '100%',
  alignItems: 'center',
  alignSelf: 'center',
  gap: theme.sizes.spacing['04'],
}))

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  borderRadius: theme.sizes.radius.lg,
  width: '2rem',
  height: '2rem',
  objectFit: 'cover',
}))

const UserDetails = styled(FlexColumn)(() => ({
  overflow: 'hidden',
  maxWidth: '155px',
}))

// TODO: Replace this with heading-SM variant from Typography
const Name = styled(Typography)(({ theme }) => ({
  color: theme.colors.text.inverted,
  fontSize: theme.fonts.size.sm,
  fontFamily: theme.fonts.family.body,
  fontWeight: theme.fonts.weight.medium,
  lineHeight: theme.fonts.lineHeight['leading-5'],
  letterSpacing: theme.fonts.letterSpacing.wide,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  '&.listItem': {
    color: theme.colors.text.primary,
  },
}))

const TeamOrDescription = styled(Typography)(({ theme }) => ({
  color: theme.colors.text.inverted,
  fontSize: theme.fonts.size.xs,
  fontFamily: theme.fonts.family.body,
  fontWeight: theme.fonts.weight.normal,
  lineHeight: theme.fonts.lineHeight['leading-3'],
  letterSpacing: theme.fonts.letterSpacing.wide,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  '&.listItem': {
    color: theme.colors.text.primary,
  },
}))

const StyledListItemIcon = styled(MuiListItemIcon)(({ theme }) => ({
  minWidth: '2rem',
  color: theme.colors.icon.tertiary,
  '.MuiListItemButton-root:hover &': {
    color: theme.colors.text.tertiary,
  },
}))
const LogoutIcon = styled(LogoutOutlinedIcon)(() => ({
  width: '20px',
  height: '20px',
  color: 'inherit',
}))

const SettingsIcon = styled(SettingsOutlinedIcon)(() => ({
  width: '20px',
  height: '20px',
  color: 'inherit',
}))

const ChangeTeamIcon = styled(SyncAltOutlinedIcon)(() => ({
  width: '20px',
  height: '20px',
  color: 'inherit',
}))

const AddTeamIcon = styled(AddIcon)(() => ({
  width: '20px',
  height: '20px',
  color: 'inherit',
}))

const StyledSelect = styled(MuiSelect)(({ theme }) => ({
  width: '100%',
  margin: theme.sizes.spacing.none,
  padding: theme.sizes.spacing.none,
  '& .MuiSelect-select': {
    padding: `${theme.sizes.spacing['02']} ${theme.sizes.spacing['04']}`,
    fontSize: theme.fonts.size.sm,
    fontFamily: theme.fonts.family.body,
    fontWeight: theme.fonts.weight.medium,
    lineHeight: theme.fonts.lineHeight['leading-5'],
    letterSpacing: theme.fonts.letterSpacing.wide,
  },
  '& .MuiSelect-icon': {
    color: theme.colors.icon.tertiary,
  },
}))

const StyledMenuItem = styled(MuiMenuItem)(({ theme }) => ({
  fontSize: theme.fonts.size.sm,
  fontFamily: theme.fonts.family.body,
  fontWeight: theme.fonts.weight.medium,
  lineHeight: theme.fonts.lineHeight['leading-5'],
  letterSpacing: theme.fonts.letterSpacing.wide,
  color: theme.colors.text.primary,
  '&.Mui-selected': {
    backgroundColor: theme.colors.surface.brand,
    color: theme.colors.text.inverted,
  },
  '&:hover': {
    backgroundColor: theme.colors.surface.hover,
    color: theme.colors.text.primary,
  },
}))

const MenuContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  transform: 'translate(0, -100%)',
  padding: theme.sizes.spacing['04'],
  overflow: 'hidden',
  '&:not(.showMenu)': {
    display: 'none',
  },
}))

const MenuItemsWrapper = styled(FlexColumn)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.colors.surface.primary,
  padding: theme.sizes.spacing['04'],
  overflow: 'hidden',
  borderRadius: theme.sizes.radius.lg,
  gap: theme.sizes.spacing['01'],
}))

const MenuListItem = styled(FlexRow)(({ theme }) => ({
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.sizes.spacing['06'],
  paddingLeft: theme.sizes.spacing['02'],
  paddingRight: theme.sizes.spacing['07'],
  height: '2.5rem',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  borderRadius: theme.sizes.radius.sm,
  '&:hover': {
    backgroundColor: theme.colors.surface.hover,
  },
  '&:hover .iconContainer, &:hover .menuText': {
    color: theme.colors.text.brand,
  },
}))

const IconContainer = styled(Box)(({ theme }) => ({
  width: theme.sizes.spacing['09'],
  height: theme.sizes.spacing['09'],
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.colors.icon.tertiary,
}))

export {
  ChangeTeamIcon,
  AddTeamIcon,
  ExpandIcon,
  LogoutIcon,
  MenuHeaderButton,
  MenuItemButton,
  MenuWrapper,
  Name,
  SettingsIcon,
  StyledAvatar,
  StyledList,
  StyledListItem,
  StyledListItemIcon,
  TeamOrDescription,
  UserDetails,
  UserItem,
  StyledSelect,
  StyledMenuItem,
  UserButtonWrapper,
  MenuContainer,
  MenuItemsWrapper,
  MenuListItem,
  IconContainer,
}
